import { useState, useEffect, useRef, useCallback } from 'react';
import api from 'services/api';
import { identiteAnalyse } from './identiteAnalyse';

const STYLES = {
  panel: { marginTop: 16, padding: 16, background: '#FEFCE8', border: '1px solid #FDE68A', borderRadius: 8 },
  header: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 },
  badge: { background: 'linear-gradient(135deg, #8B1A1A, #C8A84E)', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700 },
  title: { fontSize: 13, fontWeight: 600, color: '#92400E' },
  text: { fontSize: 12, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  list: { margin: '8px 0', paddingLeft: 20, fontSize: 12, color: '#333', lineHeight: 1.8 },
  score: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 16, fontWeight: 700, fontSize: 14 },
  btn: { padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer', background: 'linear-gradient(135deg, #8B1A1A, #C8A84E)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: '#1E293B' },
};

function scoreColor(score) {
  if (score >= 80) return '#16A34A';
  if (score >= 60) return '#CA8A04';
  if (score >= 40) return '#EA580C';
  return '#DC2626';
}

export default function IAAnalysisPanel({ type, data, buttonLabel, disclaimer, autoAnalyze }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // ⚠️ CLÉ DE VALEUR, PAS D'IDENTITÉ. Les appelants passent `data` en objet
  //    littéral inline : sa référence change à chaque rendu du parent. Une
  //    dépendance sur `data` remettrait le panneau à zéro en permanence.
  const identite = identiteAnalyse(type, data);

  // ⚠️ `autoFait` REMPLACE L'ANCIEN `didAuto` BOOLÉEN, et c'est mieux. Le
  //    booléen ne retenait QUE « une analyse automatique a eu lieu » — jamais
  //    SUR QUOI. Il fallait donc penser à le rouvrir à la main quand l'objet
  //    changeait. Ici on retient L'IDENTITÉ déjà auto-analysée : la comparaison
  //    porte sur le fait, plus sur une intention qu'on peut oublier.
  const autoFait = useRef(null);

  // ⚠️ LES PROPS COURANTES DANS UNE RÉFÉRENCE, pour qu'`analyze` soit STABLE.
  //    Sans cela `analyze` se redéfinit à chaque rendu, et ESLint le refuse à
  //    juste titre : « makes the dependencies of useEffect change on every
  //    render ». L'affectation se fait pendant le rendu, donc avant tout effet :
  //    `analyze` lit toujours les valeurs du rendu courant.
  const propsCourantes = useRef({ type, data });
  propsCourantes.current = { type, data };

  const analyze = useCallback(async () => {
    const { type: t, data: d } = propsCourantes.current;
    setLoading(true);
    setError('');
    try {
      const r = await api.post(`/ia/analyze/${t}`, d);
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'analyse IA');
    } finally { setLoading(false); }
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════
   * INVALIDATION — LA JUSTESSE NE DÉPEND D'AUCUN APPELANT
   * ═══════════════════════════════════════════════════════════════════════
   * ⚠️ LE DÉFAUT QUE CECI FERME, mesuré le 13/09/2026. `result` n'était JAMAIS
   *    remis à zéro quand `data` changeait. Sur les trois montages de PAGE —
   *    sans modale, donc sans démontage — le panneau continuait d'afficher
   *    l'analyse de l'objet PRÉCÉDENT sous l'intitulé du nouveau :
   *      · ExportFECPage  — audit de l'exercice 2025 affiché sous 2026
   *      · BalancePage    — analyse d'une période affichée sous une autre
   *      · PayrollPage    — écarts d'un mois affichés sous un autre
   *    Et c'était VERROUILLÉ : le bouton étant masqué par `{!result && …}`,
   *    l'utilisateur ne pouvait même pas relancer l'analyse.
   *
   * ⚠️ PAR INVALIDATION, PAS PAR `key` (arbitrage de Dom). Un `key` déporterait
   *    la justesse sur NEUF appelants : il suffirait qu'un dixième l'oublie pour
   *    que le défaut revienne, en silence. Ici le composant se protège seul.
   *    ⓘ `PayrollPage:751` porte déjà un `key` et était donc sain — il le reste,
   *      et il n'est plus le seul à l'être.
   *
   * ⓘ CET EFFET NE S'OCCUPE QUE DE JETER. C'est l'effet suivant qui décide de
   *   relancer, en comparant l'identité déjà auto-analysée (`autoFait`) à
   *   l'identité courante — les deux sont indépendants, et aucun ne dépend de
   *   l'ordre dans lequel React les joue.
   * ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    setResult(null);
    setError('');
  }, [identite]);

  // ⓘ AUCUN `eslint-disable` NULLE PART DANS CE FICHIER — c'était le point de
  //   départ du lot. Les trois dépendances sont DÉCLARÉES et toutes utilisées :
  //   `autoAnalyze` et `identite` dans le corps, `analyze` à l'appel. `analyze`
  //   est stable (`useCallback` sans dépendance), donc cet effet ne se rejoue
  //   qu'au vrai changement — pas à chaque rendu du parent.
  useEffect(() => {
    if (!autoAnalyze) return;
    if (autoFait.current === identite) return;
    autoFait.current = identite;
    analyze();
  }, [autoAnalyze, identite, analyze]);

  return (
    <div>
      {/* ⚠️ LE VERROU EST RETIRÉ. Ce bouton était masqué par `{!result && …}` :
          une fois un résultat affiché, l'utilisateur ne pouvait PLUS relancer
          l'analyse. Conjugué au résultat jamais invalidé, cela verrouillait une
          donnée périmée à l'écran — sur l'écran de la fonctionnalité qu'on vend.
          ⓘ Il reste `disabled` PENDANT l'analyse : on empêche le double envoi,
            on n'empêche pas de recommencer. */}
      <button onClick={analyze} disabled={loading} style={{ ...STYLES.btn, opacity: loading ? 0.6 : 1 }}>
        <span style={{ fontSize: 14 }}>&#10024;</span>
        {loading
          ? 'Analyse en cours...'
          : (result ? 'Relancer l\'analyse' : (buttonLabel || 'Analyser avec l\'IA'))}
      </button>

      {error && <p style={{ color: '#DC2626', fontSize: 12, marginTop: 8 }}>{error}</p>}

      {result && (
        <div style={STYLES.panel}>
          <div style={STYLES.header}>
            <span style={STYLES.badge}>IA</span>
            <span style={STYLES.title}>Analyse Intelligence Artificielle</span>
          </div>

          {/* Score display */}
          {result.score !== undefined && (
            <div style={{ marginBottom: 12 }}>
              <span style={{ ...STYLES.score, background: `${scoreColor(result.score)}15`, color: scoreColor(result.score) }}>
                {result.score}/100
              </span>
              {result.badge && <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: scoreColor(result.score) }}>{result.badge}</span>}
              {result.niveau && <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: scoreColor(result.score) }}>{result.niveau}</span>}
            </div>
          )}

          {/* Text fields */}
          {result.explication && <p style={STYLES.text}><strong>Explication :</strong> {result.explication}</p>}
          {result.base_legale && <p style={STYLES.text}><strong>Base legale :</strong> {result.base_legale}</p>}
          {result.resume_impact && <p style={STYLES.text}><strong>Impact :</strong> {result.resume_impact}</p>}
          {result.resume && <p style={STYLES.text}><strong>Resume :</strong> {result.resume}</p>}
          {result.synthese && <p style={STYLES.text}><strong>Synthese :</strong> {result.synthese}</p>}
          {result.cause_probable && <p style={STYLES.text}><strong>Cause probable :</strong> {result.cause_probable}</p>}
          {result.action_recommandee && <p style={STYLES.text}><strong>Action recommandee :</strong> {result.action_recommandee}</p>}
          {result.mesure_prevention && <p style={STYLES.text}><strong>Mesure de prevention :</strong> {result.mesure_prevention}</p>}
          {result.analyse && typeof result.analyse === 'string' && <p style={STYLES.text}><strong>Analyse :</strong> {result.analyse}</p>}
          {result.bilan && <p style={STYLES.text}><strong>Bilan :</strong> {result.bilan}</p>}
          {result.action_commerciale && <p style={STYLES.text}><strong>Action commerciale :</strong> {result.action_commerciale}</p>}
          {result.text && !result.explication && !result.resume && !result.synthese && !result.analyse && (
            <p style={STYLES.text}>{result.text}</p>
          )}
          {result.conformite && (
            <p style={STYLES.text}><strong>Conformite :</strong>{' '}
              <span style={{ fontWeight: 700, color: result.conformite === 'conforme' ? '#16A34A' : result.conformite === 'attention' ? '#CA8A04' : '#DC2626' }}>
                {result.conformite.toUpperCase()}
              </span>
              {result.pret_export !== undefined && <span style={{ marginLeft: 8, fontSize: 11 }}>({result.pret_export ? 'Pret pour export' : 'Corrections requises'})</span>}
            </p>
          )}
          {result.risque_resiliation !== undefined && (
            <p style={STYLES.text}><strong>Risque de resiliation :</strong> <span style={{ color: result.risque_resiliation ? '#DC2626' : '#16A34A', fontWeight: 600 }}>{result.risque_resiliation ? 'OUI' : 'Non'}</span></p>
          )}

          {/* Lists */}
          {result.points_cles?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Points cles :</strong>
              <ul style={STYLES.list}>{result.points_cles.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.changements?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Changements :</strong>
              <ul style={STYLES.list}>{result.changements.map((c, i) => <li key={i}><strong>{c.composante}</strong> : {c.avant} → {c.apres} — {c.explication}</li>)}</ul>
            </div>
          )}
          {result.alertes?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Alertes :</strong>
              <ul style={STYLES.list}>{result.alertes.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.ecarts?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Ecarts detectes :</strong>
              <ul style={STYLES.list}>{result.ecarts.map((e, i) => <li key={i}><strong>{e.agent}</strong> ({e.priorite}) — {e.explication}{e.action ? ` → ${e.action}` : ''}</li>)}</ul>
            </div>
          )}
          {result.recommandations?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Recommandations :</strong>
              <ul style={STYLES.list}>{result.recommandations.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.points_forts?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Points forts :</strong>
              <ul style={STYLES.list}>{result.points_forts.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.axes_amelioration?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Axes d'amelioration :</strong>
              <ul style={STYLES.list}>{result.axes_amelioration.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.points_vigilance?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Points de vigilance :</strong>
              <ul style={STYLES.list}>{result.points_vigilance.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.points_attention?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Points d'attention :</strong>
              <ul style={STYLES.list}>{result.points_attention.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.comptes_anormaux?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Comptes anormaux :</strong>
              <ul style={STYLES.list}>{result.comptes_anormaux.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.anomalies?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Anomalies :</strong>
              <ul style={STYLES.list}>{result.anomalies.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.verifications?.length > 0 && (
            <div><strong style={STYLES.sectionTitle}>Verifications :</strong>
              <ul style={STYLES.list}>{result.verifications.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {result.recommandation && typeof result.recommandation === 'string' && (
            <p style={{ ...STYLES.text, marginTop: 8, fontWeight: 600, color: '#8B1A1A' }}>Recommandation : {result.recommandation}</p>
          )}

          {/* Disclaimer */}
          {disclaimer && (
            <p style={{ fontSize: 11, color: '#78716C', fontStyle: 'italic', marginTop: 12, lineHeight: 1.5 }}>{disclaimer}</p>
          )}

          {/* Refresh button */}
          <button onClick={analyze} disabled={loading} style={{ ...STYLES.btn, marginTop: 8, fontSize: 11, padding: '4px 10px' }}>
            Recalculer
          </button>
        </div>
      )}
    </div>
  );
}
